/**
 * Optimizer Control Center — compact formula badge (icon + group count) with
 * the group names, per-layer weights, formula and weighted sub-formula
 * definitions revealed on hover. Colors are tuned for the dark tooltip surface
 * (bg-foreground / text-background).
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { Scale, Sigma } from 'lucide-react';
import type { FlowLayer } from '@trafficpulse/shared';

import { Tooltip } from '@/components/ui';
import type { OptimizerRow } from '@/features/optimizer-center/optimizer-center.types';

type FormulaGroup = OptimizerRow['formulaSummary']['groups'][number];

const LAYER_TITLE: Record<FlowLayer, string> = {
  paths: 'Paths',
  landers: 'Landers',
  offers: 'Offers',
};
const LAYER_ORDER: FlowLayer[] = ['paths', 'landers', 'offers'];

const GroupSummary: FC<{ group: FormulaGroup; index: number }> = ({ group, index }) => {
  const groupWeights = group.weights ?? {};
  const weightLayers = LAYER_ORDER.filter((l) => groupWeights[l] != null);
  return (
    <div className="space-y-1">
      <p className="text-xs font-semibold text-background">
        {index + 1}. {group.name}
      </p>
      {weightLayers.length > 0 && (
        <p className="flex items-center gap-1.5 text-[11px] text-background/70">
          <Scale className="h-3 w-3 shrink-0" aria-hidden />
          <span className="tabular-nums">
            {weightLayers.map((l) => `${LAYER_TITLE[l]} ${groupWeights[l]}%`).join('  ·  ')}
          </span>
        </p>
      )}
      {group.expression && (
        <p className="break-words font-mono text-[11px] text-background/75">
          {group.expression}
        </p>
      )}
      {group.subformulas.length > 0 && (
        <ul className="space-y-0.5 border-l border-background/20 pl-2">
          {group.subformulas.map((sub, i) => (
            <li key={i} className="text-[11px] leading-snug text-background/75">
              <span className="font-medium text-background/90">{sub.name}</span>
              {sub.weight != null ? (
                <span className="tabular-nums text-background/60"> ({sub.weight}%)</span>
              ) : null}
              {sub.expression ? (
                <span className="font-mono"> = {sub.expression}</span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const FormulaSummaryCell: FC<{ row: OptimizerRow }> = ({ row }) => {
  const { groupCount, groups } = row.formulaSummary;
  if (row.mode === 'STANDARD' || groupCount === 0) {
    return <span className="text-muted-foreground">—</span>;
  }

  const full = (
    <div className="space-y-2.5 text-left">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-background/60">
        {groupCount} formula group{groupCount === 1 ? '' : 's'}
      </p>
      {groups.map((group, i) => (
        <GroupSummary key={i} group={group} index={i} />
      ))}
    </div>
  );

  return (
    <Tooltip content={full} contentClassName="max-w-sm" interactive>
      <span className="inline-flex cursor-default items-center gap-1 rounded-md border border-border/60 bg-muted/30 px-1.5 py-0.5 text-xs tabular-nums text-muted-foreground">
        <Sigma className="h-3 w-3 shrink-0" aria-hidden />
        {groupCount}
      </span>
    </Tooltip>
  );
};
